/**
 * Diagram Descriptions Configuration (Issue #156)
 *
 * Static descriptions and interpretation guides for each diagram type.
 * These are used as defaults when generating diagrams and can be
 * overridden by AI-generated descriptions for more specific context.
 */
export const diagramDescriptions = {
    databaseSchema: {
        title: 'Database Schema',
        description: 'Entity-relationship diagram showing database tables, their columns, relationships, and constraints.',
        methodology: 'Parses Prisma schema files, SQL migrations, or ORM model definitions to extract table structures, column types, foreign keys, and indexes.',
        interpretationGuide: `
- Tables shown as rectangles with column listings
- Primary keys marked with PK symbol
- Foreign keys shown as arrows connecting tables
- Relationship types indicated by arrow endings (1:1, 1:N, N:M)
- Required vs optional fields distinguished by styling
    `.trim(),
        defaultLegend: [
            { symbol: '🔑', meaning: 'Primary Key', example: 'id: String @id' },
            { symbol: '🔗', meaning: 'Foreign Key', example: 'userId references users.id' },
            { symbol: '📋', meaning: 'Table/Model', example: 'User, Post' },
            { symbol: '─────', meaning: 'One-to-Many relationship' },
            { symbol: '─ ─ ─', meaning: 'Many-to-Many relationship' },
        ],
        typicalDataSources: [
            { type: 'file', path: 'prisma/schema.prisma', description: 'Prisma schema' },
            { type: 'pattern', path: 'migrations/**/*.sql', description: 'SQL migrations' },
            { type: 'pattern', path: 'src/models/**/*.ts', description: 'ORM models' },
        ],
        category: 'architecture',
        complexity: 'basic',
    },
    dependencyGraph: {
        title: 'Dependency Graph',
        description: 'Visualization of module imports and package dependencies, showing how different parts of the codebase are connected.',
        methodology: 'Analyzes import statements and package.json to build a graph of module dependencies, identifying core modules, external packages, and circular dependencies.',
        interpretationGuide: `
- Nodes represent modules, packages, or files
- Arrows point from dependent to dependency
- Node size may indicate importance (number of dependents)
- Clusters show related modules or packages
- Highlighted paths may indicate circular dependencies
    `.trim(),
        defaultLegend: [
            { symbol: '📦', meaning: 'Package/Module', example: 'lodash, react' },
            { symbol: '📄', meaning: 'Source file', example: 'utils.ts' },
            { symbol: '🔄', meaning: 'Circular dependency', example: 'A → B → A' },
            { symbol: '➡️', meaning: 'Imports/Depends on' },
        ],
        typicalDataSources: [
            { type: 'pattern', path: 'src/**/*.ts', description: 'TypeScript source files' },
            { type: 'file', path: 'package.json', description: 'Package dependencies' },
            { type: 'file', path: 'tsconfig.json', description: 'Module configuration' },
        ],
        category: 'architecture',
        complexity: 'basic',
    },
    pipelineDiagram: {
        title: 'CI/CD Pipeline',
        description: 'Visual representation of the continuous integration and deployment pipeline stages, jobs, and workflows.',
        methodology: 'Parses CI/CD configuration files (GitHub Actions, GitLab CI, CircleCI) to extract workflow stages, jobs, dependencies, and deployment targets.',
        interpretationGuide: `
- Stages flow from left to right (or top to bottom)
- Parallel jobs shown side by side
- Dependencies shown as arrows between stages
- Conditions and triggers shown as annotations
- Deployment targets highlighted at the end
    `.trim(),
        defaultLegend: [
            { symbol: '🔄', meaning: 'Build stage', example: 'Compile, bundle' },
            { symbol: '🧪', meaning: 'Test stage', example: 'Unit tests, integration' },
            { symbol: '🚀', meaning: 'Deploy stage', example: 'Production, staging' },
            { symbol: '⚙️', meaning: 'Job/Task', example: 'lint, typecheck' },
        ],
        typicalDataSources: [
            { type: 'file', path: '.github/workflows/*.yml', description: 'GitHub Actions' },
            { type: 'file', path: '.gitlab-ci.yml', description: 'GitLab CI' },
            { type: 'file', path: 'Jenkinsfile', description: 'Jenkins pipeline' },
        ],
        category: 'operations',
        complexity: 'intermediate',
    },
    apiMap: {
        title: 'API Map',
        description: 'Overview of all API endpoints, their methods, paths, and relationships.',
        methodology: 'Scans route definitions, controller files, and OpenAPI specifications to build a comprehensive map of available API endpoints.',
        interpretationGuide: `
- Endpoints grouped by resource or controller
- HTTP methods indicated by color/symbol
- Request/response types shown where available
- Authentication requirements marked
- Rate limits and permissions noted
    `.trim(),
        defaultLegend: [
            { symbol: '🟢', meaning: 'GET endpoint', example: 'Read operations' },
            { symbol: '🔵', meaning: 'POST endpoint', example: 'Create operations' },
            { symbol: '🟡', meaning: 'PUT/PATCH endpoint', example: 'Update operations' },
            { symbol: '🔴', meaning: 'DELETE endpoint', example: 'Delete operations' },
            { symbol: '🔒', meaning: 'Authenticated', example: 'Requires token' },
        ],
        typicalDataSources: [
            { type: 'pattern', path: 'src/routes/**/*.ts', description: 'Route definitions' },
            { type: 'file', path: 'openapi.yaml', description: 'OpenAPI spec' },
            { type: 'pattern', path: 'src/controllers/**/*.ts', description: 'Controllers' },
        ],
        category: 'architecture',
        complexity: 'basic',
    },
    // Behavior (Phase 2)
    callGraph: {
        title: 'Call Graph',
        description: 'Function call relationships showing how functions invoke each other throughout the codebase.',
        methodology: 'Uses AST parsing to trace function calls, building a directed graph of invocations that reveals execution flow and coupling.',
        interpretationGuide: `
- Nodes represent functions or methods
- Edges show call relationships (caller → callee)
- Entry points highlighted at the top
- Leaf functions at the bottom
- Recursive calls shown as self-loops
    `.trim(),
        defaultLegend: [
            // Node types
            { symbol: '🟢', meaning: 'Entry point', example: 'main(), handleRequest()' },
            { symbol: '🔵', meaning: 'Function', example: 'processData()' },
            { symbol: '🟩', meaning: 'Method', example: 'user.save()' },
            { symbol: '🟣', meaning: 'Arrow function', example: '() => {}' },
            { symbol: '🟡', meaning: 'Constructor', example: 'new Service()' },
            { symbol: '🔴', meaning: 'External call', example: 'lodash.map()' },
            { symbol: '🔥', meaning: 'Hotspot (frequently called)' },
            // Edge types
            { symbol: '┈┈▶', meaning: 'Async call (orange dashed)' },
            { symbol: '━━▶', meaning: 'Method call (green)' },
            { symbol: '══▶', meaning: 'Frequent call (blue, >3 times)' },
            { symbol: '──▶', meaning: 'Regular call (gray)' },
        ],
        typicalDataSources: [
            { type: 'ast', description: 'AST analysis of function calls' },
            { type: 'pattern', path: 'src/**/*.ts', description: 'Source files' },
        ],
        category: 'behavior',
        complexity: 'advanced',
    },
    // Domain & System (Phase 3 - AI-powered)
    systemContext: {
        title: 'System Context',
        description: 'C4 Level 1 diagram showing the system in its environment, including users and external systems it interacts with.',
        methodology: 'AI analyzes the entire codebase to identify external integrations, user types, and system boundaries.',
        interpretationGuide: `
- Your system is in the center
- Users/personas shown around the system
- External systems shown at the edges
- Arrows show key interactions
- This is the highest-level view
    `.trim(),
        defaultLegend: [
            { symbol: '👤', meaning: 'User/Actor', example: 'Customer, Admin' },
            { symbol: '🏢', meaning: 'Your system', example: 'VeoDiagram' },
            { symbol: '🌐', meaning: 'External system', example: 'GitHub API' },
            { symbol: '↔️', meaning: 'Interaction', example: 'Uses, Sends data to' },
        ],
        typicalDataSources: [
            { type: 'api', description: 'AI analysis of entire codebase' },
            { type: 'pattern', path: 'src/**/*', description: 'All source files' },
        ],
        category: 'architecture',
        complexity: 'basic',
    },
    deploymentPipeline: {
        title: 'Deployment Pipeline',
        description: 'End-to-end deployment process from code commit to production.',
        methodology: 'Analyzes CI/CD configurations, deployment scripts, and infrastructure-as-code.',
        interpretationGuide: `
- Stages flow from left to right
- Gates and approvals shown
- Environment deployments highlighted
- Rollback paths indicated
- Monitoring checkpoints noted
    `.trim(),
        defaultLegend: [
            { symbol: '📝', meaning: 'Code commit' },
            { symbol: '🔨', meaning: 'Build' },
            { symbol: '🧪', meaning: 'Test' },
            { symbol: '✅', meaning: 'Approval gate' },
            { symbol: '🚀', meaning: 'Deploy' },
        ],
        typicalDataSources: [
            { type: 'pattern', path: '.github/workflows/*', description: 'GitHub Actions' },
            { type: 'pattern', path: 'deploy/**/*', description: 'Deploy scripts' },
        ],
        category: 'operations',
        complexity: 'intermediate',
    },
    monitoringDashboard: {
        title: 'Monitoring Dashboard',
        description: 'Overview of system observability including metrics, logs, and traces.',
        methodology: 'AI identifies monitoring endpoints, logging configurations, and trace instrumentation.',
        interpretationGuide: `
- Key metrics displayed
- Alert thresholds shown
- Service health indicators
- Log aggregation points
- Trace correlation paths
    `.trim(),
        defaultLegend: [
            { symbol: '📈', meaning: 'Metric', example: 'Request latency' },
            { symbol: '📋', meaning: 'Logs', example: 'Application logs' },
            { symbol: '🔍', meaning: 'Traces', example: 'Request traces' },
            { symbol: '🚨', meaning: 'Alert', example: 'Error rate > 1%' },
        ],
        typicalDataSources: [
            { type: 'api', description: 'AI observability analysis' },
            { type: 'pattern', path: '**/monitoring/**/*', description: 'Monitoring configs' },
        ],
        category: 'operations',
        complexity: 'intermediate',
    },
};
/**
 * Get description for a diagram type
 */
export function getDiagramDescription(type) {
    return diagramDescriptions[type];
}
/**
 * Get all diagram types with their descriptions
 */
export function getAllDiagramDescriptions() {
    return diagramDescriptions;
}
/**
 * Get diagram types by category
 */
export function getDiagramsByCategory(category) {
    return Object.entries(diagramDescriptions)
        .filter(([, desc]) => desc.category === category)
        .map(([type]) => type);
}
/**
 * Get diagram types by complexity
 */
export function getDiagramsByComplexity(complexity) {
    return Object.entries(diagramDescriptions)
        .filter(([, desc]) => desc.complexity === complexity)
        .map(([type]) => type);
}
//# sourceMappingURL=diagramDescriptions.js.map